import { X, Copy, CheckCircle, AlertTriangle } from 'lucide-react';
import { useState } from 'react';

interface DeploymentPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  deploymentPassword: string;
  edgeName: string;
}

const DeploymentPasswordModal = ({
  isOpen,
  onClose,
  deploymentPassword,
  edgeName,
}: DeploymentPasswordModalProps) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(deploymentPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-yellow-200 bg-yellow-50">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-yellow-600" />
            <h2 className="text-xl font-semibold text-gray-900">Edge Approved - Deployment Password</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-yellow-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Warning Banner */}
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-1">⚠️ This password will only be shown once</h3>
                <p className="text-sm text-red-800">
                  Copy this deployment password and provide it to the edge technician or save it securely. 
                  You will not be able to retrieve it again.
                </p>
              </div>
            </div>
          </div>

          {/* Edge Info */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Edge Name</label>
            <p className="text-lg font-semibold text-gray-900">{edgeName}</p>
          </div>

          {/* Deployment Password */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Deployment Password</label>
            <div className="flex items-center space-x-2">
              <div className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg">
                <code className="text-2xl font-mono font-bold text-gray-900 tracking-wider">
                  {deploymentPassword}
                </code>
              </div>
              <button
                onClick={handleCopy}
                className={`px-4 py-3 rounded-lg font-medium transition-all ${
                  copied
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                title="Copy to clipboard"
              >
                {copied ? (
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>Copied!</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Copy className="w-5 h-5" />
                    <span>Copy</span>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Next Steps:</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              <li>Copy the deployment password above</li>
              <li>Provide it to the edge technician or automated provisioning system</li>
              <li>The edge will use this password to download its certificate bundle</li>
              <li>Once provisioned, the edge will establish secure mTLS connection</li>
            </ol>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            I've Saved the Password
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeploymentPasswordModal;

